import _ from 'lodash';
import {
  getNetworkBrandingName,
  isMovie,
  NEWS_GENRE,
  PAGE_TYPE_ARTICLE,
  PAGE_TYPE_CREDITS,
  PAGE_TYPE_SEE_ALL,
  TAB_PRIMETIME,
} from 'helpers/ContentHelper';
import {
  helperGetFNCLiveUrl,
  helperGetHomeUrl,
  helperGetNewsUrl,
  helperGetPersonalitiesUrl,
  helperGetShowsUrl,
  helperGetSportsUrl,
  helperGetBrowseUrl,
  helperGetWatchlistUrl,
} from 'helpers/RouteHelper';
import {
  generateItem,
  getArticleData,
  getBrowseData,
  getCreditsData,
  getCreditsDetailData,
  getMovieData,
  getPrimetimeData,
  getSeasonData,
  getSeeAllData,
  getSeriesArticleData,
  getShowData,
  getWatchData,
} from './BreadcrumbsHelper';

const {
  showsPage_header: showsLabel,
  browsePage_header: browseLabel,
  personalitiesPage_header: personalitiesLabel,
  badge_liveText: liveLabel,
  newsPage_header: newsLabel,
  sportsPage_header: sportsLabel,
  watchlistPage_breadcrumbTitle: watchlistLabel,
} = window.Language;

const BRANDING_NAME = getNetworkBrandingName();

const HOME_ITEM = generateItem(helperGetHomeUrl(), BRANDING_NAME);
const BROWSE_ITEM = generateItem(helperGetBrowseUrl(), browseLabel);
const SHOWS_ITEM = generateItem(helperGetShowsUrl(), showsLabel);
const LIVE_NATION_NEWS_ITEM = generateItem(helperGetFNCLiveUrl(), liveLabel);
const PERSONALITIES_ITEM = generateItem(helperGetPersonalitiesUrl(), personalitiesLabel);
const NEWS_ITEM = generateItem(helperGetNewsUrl(), newsLabel);
const SPORTS_ITEM = generateItem(helperGetSportsUrl(), sportsLabel);
const WATCHLIST_ITEM = generateItem(helperGetWatchlistUrl(), watchlistLabel);

export const articleDetailParser = (articleData, params) => {
  if (!articleData) return null;
  const data = [HOME_ITEM];
  data.push(getArticleData(articleData, params));
  return data;
};

export const browseParser = (selectedTab, filterName) => {
  const data = [HOME_ITEM, BROWSE_ITEM];
  if (filterName) {
    data.push(getBrowseData(filterName, selectedTab));
  }
  return data;
};

export const collectionParser = route => {
  const { path } = route;
  if (path.includes('featured')) {
    return [HOME_ITEM];
  }
  if (path.includes('shows')) {
    return [HOME_ITEM, SHOWS_ITEM];
  }
  return [HOME_ITEM, generateItem(`/${path}/`, _.capitalize(path))];
};

export const channelParser = channelId => {
  let channelName = _.capitalize(channelId);
  const parsedChannel = channelId.split('-');
  if (parsedChannel.length > 1) {
    channelName = parsedChannel.map(item => _.capitalize(item)).join(' ');
  }
  return [HOME_ITEM, generateItem(`/channel/${channelId}/`, channelName)];
};
export const liveParser = selectedTab => {
  const liveItem = LIVE_NATION_NEWS_ITEM;
  const liveData = [HOME_ITEM, liveItem];
  if (selectedTab === TAB_PRIMETIME) {
    liveData.push(getPrimetimeData());
  }
  return liveData;
};

export const seriesDetailParser = (
  params,
  seriesData,
  articleData,
  creditsData,
  isPersonality,
  headline,
) => {
  if (!seriesData) return null;
  const { pageType, pageId } = params;
  const {
    id: showId,
    name: showName,
    seriesType,
    seriesDisplayType,
    genres,
    subGenres,
  } = seriesData;
  const siteScanUrl = articleData?.siteScanUrl;
  const shouldDisplayNews =
    seriesDisplayType === 'news' || _.includes(subGenres, 'News and Information');
  const shouldDisplaySports = _.some(
    genres,
    genre => genre.toLowerCase() === 'sports' || genre === 'Sports and Fitness',
  );
  const showData = getShowData({ showId, showName, isPersonality, seriesType });

  let secondLevelBreadcrumb = SHOWS_ITEM;
  if (isPersonality) secondLevelBreadcrumb = PERSONALITIES_ITEM;
  if (shouldDisplayNews && NEWS_ITEM) secondLevelBreadcrumb = NEWS_ITEM;
  if (shouldDisplaySports && SPORTS_ITEM) secondLevelBreadcrumb = SPORTS_ITEM;
  const seriesDetailData = [HOME_ITEM, secondLevelBreadcrumb, showData];

  const shouldAddArticle = pageType === PAGE_TYPE_ARTICLE && articleData;
  const shouldAddSeriesArticle = siteScanUrl && siteScanUrl.split('/').pop() === pageType;
  const shouldAddCredits = pageType === PAGE_TYPE_CREDITS && showId;
  const shouldAddCreditsDetail = shouldAddCredits && creditsData;
  const shouldAddSeason = _.includes(pageType, 'season-');
  const shouldAddSeeAll = pageType === PAGE_TYPE_SEE_ALL && pageId;

  if (shouldAddArticle) {
    seriesDetailData.push(getArticleData(articleData, params));
  }

  if (shouldAddSeriesArticle) {
    seriesDetailData.push(getSeriesArticleData(articleData));
  }

  if (shouldAddCredits) {
    seriesDetailData.push(getCreditsData(showId));
  }

  if (shouldAddCreditsDetail) {
    seriesDetailData.push(getCreditsDetailData(showId, pageId, creditsData));
  }

  if (shouldAddSeason) {
    const seasonNumber = _.replace(pageType, 'season-', '');
    seriesDetailData.push(getSeasonData(showId, seasonNumber));
  }

  if (shouldAddSeeAll) {
    seriesDetailData.push(getSeeAllData(showId, headline));
  }

  return seriesDetailData;
};

export const watchParser = video => {
  if (!video) return null;
  const {
    contentSKU,
    currentSeason,
    genres,
    seasonNumber,
    seriesDisplayType,
    seriesName: showName,
    seriesType,
    showCode: showId,
    subGenres,
    videoType,
  } = video;
  const shouldDisplayNews =
    seriesDisplayType === 'news' ||
    _.includes(subGenres, NEWS_GENRE) ||
    _.some(genres, genre => genre === NEWS_GENRE) ||
    _.some(contentSKU, sku => sku === 'us.FoxNews');

  const shouldDisplaySports = _.some(
    genres,
    genre => genre.toLowerCase() === 'sports' || genre === 'Sports and Fitness',
  );
  const isMovieData = isMovie(videoType) || isMovie(seriesType);

  let secondLevelBreadcrumb = SHOWS_ITEM;
  if (shouldDisplayNews && NEWS_ITEM) secondLevelBreadcrumb = NEWS_ITEM;
  if (shouldDisplaySports && SPORTS_ITEM) secondLevelBreadcrumb = SPORTS_ITEM;

  const showData = getShowData({ showId, showName, seriesType });
  const movieData = getMovieData(showId, showName);
  const seasonData = currentSeason === seasonNumber ? null : getSeasonData(showId, seasonNumber);
  const showTabData = seasonData;
  const watchData = getWatchData(video);

  if (isMovieData) {
    return [HOME_ITEM, SHOWS_ITEM, movieData, watchData];
  }
  return [HOME_ITEM, secondLevelBreadcrumb, showData, showTabData, watchData];
};

export const watchlistParser = () => [HOME_ITEM, WATCHLIST_ITEM];
